/**
 * The footer under a paged grid: which rows are showing out of how many, the
 * pager, and the rows per page (see .data-grid__footer in _pagination.scss).
 *
 * The grid owns the state; this only draws it. Clicks on the pager come back
 * through pagerTarget(), and the select is named `perPageName`.
 */

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

// how many page numbers the pager shows at once, around the current page
const PAGER_SPAN = 5;

const renderPager = (currentPage, maxPages, label) => {

  const lastPage = maxPages - 1;
  const atStart = currentPage === 0;
  const atEnd = currentPage === lastPage;

  const from = Math.max(0, Math.min(currentPage - 2, maxPages - PAGER_SPAN));
  const to = Math.min(lastPage, from + PAGER_SPAN - 1);

  const step = (page, title, icon, disabled) => `
      <button type="button" class="data-grid__pager-button data-grid__pager-button--step" data-page="${page}" title="${title}" aria-label="${title}" ${disabled ? 'disabled' : ''}>
        <i class="fas fa-${icon}" aria-hidden="true"></i>
      </button>`;

  let pages = '';

  for (let page = from; page <= to; page++) {
    const isCurrent = page === currentPage;

    pages += `
      <button type="button" class="data-grid__pager-button${isCurrent ? ' is-active' : ''}" data-page="${page}" aria-label="Page ${page + 1}"${isCurrent ? ' aria-current="page"' : ''}>${page + 1}</button>`;
  }

  return `
    <nav class="data-grid__pager" aria-label="${label}">
      ${step('first', 'First page', 'angle-double-left', atStart)}
      ${step('previous', 'Previous page', 'angle-left', atStart)}
      ${pages}
      ${step('next', 'Next page', 'angle-right', atEnd)}
      ${step('last', 'Last page', 'angle-double-right', atEnd)}
    </nav>`;
};

/**
 * @param {object} state
 * @param {number} state.total          rows across every page
 * @param {number} state.currentPage    zero-based
 * @param {number} state.resultsPerPage
 * @param {number} state.maxPages       at least 1
 * @param {string} state.singular       "sensor"
 * @param {string} state.plural         "sensors"
 * @param {string} state.perPageName    the name of the rows-per-page select
 * @returns {string} HTML
 */
export const renderGridFooter = ({ total, currentPage, resultsPerPage, maxPages, singular, plural, perPageName }) => {

  const start = currentPage * resultsPerPage + 1;
  const end = Math.min((currentPage + 1) * resultsPerPage, total);

  const range = total === 0
    ? `No ${plural} to show`
    : `Showing <strong>${start}&ndash;${end}</strong> of <strong>${total}</strong> ${total === 1 ? singular : plural}`;

  const options = PER_PAGE_OPTIONS
    .map(count => `<option value="${count}"${count === Number(resultsPerPage) ? ' selected' : ''}>${count}</option>`)
    .join('');

  // A short list fits on one page whatever is picked here, so a small grid
  // gets a footer of just its count rather than a control that does nothing.
  const perPage = total > PER_PAGE_OPTIONS[0] ? `
      <label class="data-grid__per-page">
        Show
        <select name="${perPageName}">${options}</select>
        per page
      </label>` : '';

  return `
    <div class="data-grid__footer">

      <div class="data-grid__range" role="status">${range}</div>

      ${maxPages > 1 ? renderPager(currentPage, maxPages, `Pages of ${plural}`) : ''}
      ${perPage}

    </div>
  `;
};

/**
 * The page a click on one of the pager's buttons asks for: its data-page is
 * first / previous / next / last, or a page index.
 */
export const pagerTarget = (button, currentPage, maxPages) => {

  switch (button.dataset.page) {
    case 'first':
      return 0;
    case 'previous':
      return currentPage - 1;
    case 'next':
      return currentPage + 1;
    case 'last':
      return maxPages - 1;
    default:
      return parseInt(button.dataset.page, 10) || 0;
  }

};
